import { useMemo } from 'react'

import { Venue, VenueEvent } from 'types/model'

import { useVenue } from '@modules/venue/data'
import { useVenueEvents } from '@modules/event/data'
import { EventCard } from '@modules/event/views/event-card'
import { Carousel } from '@components/carousel'
import { getVenuePurchaseRangeMs } from '@utils/date-time'

export interface EventCarouselProps {
  venueId: string
  layout?: 'vertical' | 'horizontal'
  venue?: Venue
  events?: VenueEvent[]
  onCardClick: (eventId: string) => void
}

export function EventCarousel({
  venueId,
  layout = 'vertical',
  venue: _venue,
  events: _events,
  onCardClick,
}: EventCarouselProps) {
  const { venue, events } = useEventCarouselController(venueId, {
    fallbackData: { venue: _venue, events: _events },
  })

  const venueData = venue.data

  if (venueData === undefined || !events.data)
    return (
      <Carousel>
        {Array(6)
          .fill(0)
          .map((_, index) => (
            <EventCard.Skeleton key={index} />
          ))}
      </Carousel>
    )
  return (
    <div className="w-full">
      {venueData !== undefined && (
        <Carousel itemClassName="min-w-full xs:min-w-min">
          {events.data.map((venueEvent, index) => (
            <EventCard
              key={`${venueEvent.id}-${index}`}
              name={venueEvent.name}
              featured={venueEvent.featured}
              eventId={venueEvent.id}
              dateTime={venueEvent.dateStart}
              timezone={venueEvent.timezone || venueData.location.timezone}
              poster={venueEvent.primaryImage?.filename}
              venueName={venueData.name}
              venueAddress={venueData.location.address}
              layout={layout}
              onClick={onCardClick}
            />
          ))}
        </Carousel>
      )}
    </div>
  )
}

function useEventCarouselController(
  venueId: string,
  {
    fallbackData,
  }: { fallbackData?: { venue?: Venue; events?: VenueEvent[] } } = {}
) {
  const { venue: _venue, events: _events } = fallbackData ?? {}

  const venue = useVenue(venueId, {
    config: {
      fallbackData: _venue,
    },
  })

  const rangeVenue = _venue ?? venue.data
  const purchaseWindow = rangeVenue?.inventoryTimeframe ?? 30
  const venueTimezone = rangeVenue?.location?.timezone

  const [dateStart, dateEnd] = useMemo(() => {
    const [startMs, endMs] = getVenuePurchaseRangeMs(
      Date.now(),
      purchaseWindow,
      venueTimezone
    )
    return [String(startMs), String(endMs)] as [string, string]
  }, [purchaseWindow, venueTimezone])

  const events = useVenueEvents(venueId, {
    params: {
      dateStart,
      dateEnd,
      limit: purchaseWindow * 2,
      skip: 0,
      sort: 'dateStart',
    },
    config: {
      fallbackData: _events,
      // Avoid duplicate /events calls on venue home when SSR already provided the list.
      revalidateOnMount: !(_events && _events.length > 0),
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  })

  return {
    venue,
    events,
  }
}
